// import React, { createContext, useContext, useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// const AuthCtx = createContext({
//   user: null,
//   login: () => {},
//   logout: () => {},
// });

// export function useAuth() { return useContext(AuthCtx); }

// export default function AuthProvider({ children }) {
//   const [user, setUser] = useState(null);
// const navigate = useNavigate();
//   useEffect(() => {
//     const raw = localStorage.getItem("bb_user");
//     if (raw) try { setUser(JSON.parse(raw)); } catch {}
//   }, []);

//   const login = (u, token) => {
//     const data = { ...u, token };
//     debugger
//     setUser(data);
//     // localStorage.setItem("bb_user", JSON.stringify(data));
//   };
//   const logout = () => {
//     setUser(null);
//     localStorage.removeItem("bb_user");
//     navigate('/')
//   };
// debugger
//   return (
//     <AuthCtx.Provider value={{ user, login, logout }}>
//       {children}
//     </AuthCtx.Provider>
//   );
// }

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchUserProfile,
  getStoredAuthUser,
  getStoredToken,
  getStoredUserId,
} from "../lib/userProfile.js";

const AuthCtx = createContext({
  user: null,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
  refreshUser: async () => {},
});

export function useAuth() {
  return useContext(AuthCtx);
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  /* OTP/API mismatch: login UI saves `phone`, Mongo/API use `mobile` — mirror both as 10 digits for checkout & Razorpay. */
  function withSyncedContact(state) {
    if (!state || typeof state !== "object") return state;
    const raw = state.mobile ?? state.phone ?? state.contact ?? "";
    let d = String(raw).replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
    if (d.length > 10) d = d.slice(-10);
    if (d.length !== 10) return state;
    if (state.mobile === d && state.phone === d) return state;
    return { ...state, mobile: d, phone: d };
  }

  /* ---------------- helpers ---------------- */
  const saveUser = (next) => {
    console.log("🟢 [Auth] saveUser:", next);
    const synced = next ? withSyncedContact(next) : null;
    setUser(synced);
    if (synced) {
      localStorage.setItem("bb_user", JSON.stringify(synced));
    } else {
      localStorage.removeItem("bb_user");
    }
  };

  /* ---------------- initial load ---------------- */
  useEffect(() => {
    const raw = localStorage.getItem("bb_user");
    if (!raw) {
      console.log("⚪ [Auth] no bb_user in localStorage");
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      console.log("🟢 [Auth] loaded from localStorage:", parsed);
      const synced = withSyncedContact(parsed);
      setUser(synced);
      localStorage.setItem("bb_user", JSON.stringify(synced));
    } catch (err) {
      console.error("❌ [Auth] parse bb_user failed:", err);
      localStorage.removeItem("bb_user");
    }
  }, []);

  /* ---------------- cross-tab sync ---------------- */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "bb_user") return;
      console.log("🔄 [Auth] storage event bb_user:", e.newValue);
      if (e.newValue) {
        try {
          const p = JSON.parse(e.newValue);
          setUser(withSyncedContact(p));
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* ---------------- public api ---------------- */
  const login = (u, token) => {
    const data = { ...u, token };
    console.log("🔐 [Auth] login():", data);
    saveUser(data);
    // post-login: pull fresh profile once
    setTimeout(() => {
      console.log("🔁 [Auth] post-login refreshUser()");
      refreshUser();
    }, 0);
  };

  const logout = () => {
    console.log("🚪 [Auth] logout()");
    saveUser(null);
    navigate("/");
  };

  // merge partial fields (e.g., name, email, profileImage)
  const updateUser = (partial) => {
    console.log("🟨 [Auth] updateUser() partial:", partial);
    setUser((prev) => {
      const merged = { ...(prev || {}), ...(partial || {}) };
      const next = withSyncedContact(merged);
      console.log("✅ [Auth] merged user:", next);
      localStorage.setItem("bb_user", JSON.stringify(next));
      return next;
    });
  };

  // fetch latest profile from server and persist
  const refreshUser = useCallback(async () => {
    try {
      const parsed = getStoredAuthUser();
      const token = getStoredToken(parsed);
      const userId = getStoredUserId(parsed);
      console.log("🔄 [Auth] refreshUser() with token:", !!token, "userId:", userId);

      if (!token || !userId) {
        console.warn("⚠️ [Auth] refreshUser aborted – token/userId missing");
        return null;
      }

      const profile = await fetchUserProfile(userId, token);
      if (!profile) {
        console.warn("⚠️ [Auth] refreshUser – server profile unavailable");
        return parsed;
      }

      console.log("🟢 [Auth] server profile:", profile);
      const next = withSyncedContact({ ...parsed, ...profile });
      saveUser(next);
      console.log("✅ [Auth] refreshed user saved");
      return next;
    } catch (err) {
      console.error("❌ [Auth] refreshUser failed:", err);
      return null;
    }
  }, []);

  /* ---------------- react to global 'profile:updated' event ---------------- */
  useEffect(() => {
    const onProfileUpdated = () => {
      console.log("📣 [Auth] profile:updated → refreshUser()");
      refreshUser();
    };
    window.addEventListener("profile:updated", onProfileUpdated);
    return () => window.removeEventListener("profile:updated", onProfileUpdated);
  }, [refreshUser]);

  /* ---------------- optional: refresh once on mount if logged in ---------------- */
  useEffect(() => {
    if (user?.token) {
      console.log("🧭 [Auth] initial mount → refreshUser()");
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- expose for console testing ---------------- */
  useEffect(() => {
    window.__auth = {
      getUser: () => user,
      login,
      logout,
      updateUser,
      refreshUser,
    };
    console.log("🛠️ [Auth] window.__auth ready (try __auth.getUser())");
    return () => { delete window.__auth; };
  }, [user, login, logout, updateUser, refreshUser]);

  console.log("👀 [Auth] render user:", user);

  return (
    <AuthCtx.Provider value={{ user, login, logout, updateUser, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
}
